/**
 * ================================================================================================
 *  טבלת ההמלצות לחיוב — EVERY CUSTOMER FOR THE PERIOD, FILTERED AND SORTED LIKE A SPREADSHEET
 * ================================================================================================
 *
 * The Owner's specification, 2026-11-03:
 *
 *     *"הצג כברירת מחדל את כל הלקוחות לתקופת החיוב שנבחרה... אם אין חיוב, הצג את הסיבה... הצג
 *      0 ₪ רק כשהחישוב באמת יצא אפס."*
 *
 *     *"הוסף סינון ומיון בכל כותרת עמודה, כמו באקסל, לפי סוג הנתון... אפשר לשלב כמה מסננים,
 *      לראות אילו מסננים פעילים ולנקות את כולם בלחיצה אחת."*
 *
 *     *"סמן מראש רק שורות שמוכנות לחיוב, ואפשר לי להוריד מהן V. אם סינון מסתיר לקוחות שכבר
 *      מסומנים, הצג כמה מהם מוסתרים ותן לי להסיר אותם מהבחירה."*
 *
 *     *"שורת הסיכום תהיה השורה התחתונה של טבלת החיובים עצמה, באותה מסגרת וביישור לעמודות. היא
 *      תישאר גלויה בתחתית אזור הטבלה בזמן גלילת הלקוחות."*
 *
 * ── IT USES THE SYSTEM'S GRID, AND THE FIRST VERSION DID NOT ──────────────────────────────────
 *
 * **MEASURED IN A BROWSER, 2026-11-03.** The first version invented its own class names —
 * `table-scroll`, `th-row`, `is-end` — none of which exists in `styles.css`. Every one of them
 * styled nothing, so the browser laid the table out with defaults: the filter control dropped onto
 * a line of its own and doubled the header height, dates broke mid-string (`2026-10-` / `31`), the
 * amount separated from its shekel sign, and THE PAGE scrolled sideways instead of the table.
 *
 * A CLASS THAT STYLES NOTHING IS WORSE THAN NO CLASS: the markup reads as designed, the review
 * reads as designed, and only a rendered screen disagrees. `.grid` is the design system's table and
 * already carries the rules this screen needs — T1 headers at the start, T2 a sticky header, T4 the
 * horizontal scrollport inside the grid and never on the page, T6 totals last and separated, A4
 * numerics never smaller than body text.
 *
 * ── THE COLUMNS ARE A LIST OF SPECIFICATIONS, NOT A WALL OF SPECIAL CASES ─────────────────────
 *
 * Eleven columns, each needing a sort, a filter of its own TYPE and a cell. Written out per column
 * that is thirty-three places to disagree. Each column declares its kind and how to read its value;
 * the filtering, the sorting and the header control are written once over that declaration.
 *
 * ── MONEY IS COMPARED IN AGOROT AND NEVER IN A JS NUMBER ──────────────────────────────────────
 *
 * The amounts are fixed-point strings for the reason `money-sum.ts` states: a price that passed
 * through a float is a rounded price. Sorting, ranges and the total parse to integer agorot.
 *
 * ── AND `0 ₪` IS NOT THE SAME AS NO FIGURE ────────────────────────────────────────────────────
 *
 *     *"הצג 0 ₪ רק כשהחישוב באמת יצא אפס."*
 *
 * `amountTotal` is `null` for a row that was never priced — blocked, skipped, not billable. A
 * `0.00` there would state that the period costs nothing, which is a different fact from *"this was
 * not priced"* and is the one an operator would act on wrongly. `null` renders as `—`.
 */

import { Fragment, useMemo, useState } from 'react';

import { StatusChip } from './StatusChip';
import { billingOutcomeHe } from '../api/billingText';
import type { RunLine, RunReport } from '../api/contract';
import { routes } from '../routes';

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   THE QUICK FILTERS
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** The outcome that may be charged. The table ticks these and nothing else. */
export const SELECTABLE_OUTCOME = 'would_charge';

/**
 * FOUR GROUPS AND AN "ALL", EACH DEFINED BY A PREDICATE OVER THE OUTCOME WORD.
 *
 * `attention` IS EVERY `blocked_*`: something is wrong and somebody has to do something — a missing
 * card, a missing billing account, a period nobody measured. `no_charge` is every `skipped_*`:
 * nothing is wrong and nothing is owed. The difference is the difference between a row an operator
 * ACTS on and a row they READ, and collapsing them is how a blocked customer goes unnoticed in a
 * list of trials.
 */
export type QuickFilter = 'all' | 'ready' | 'attention' | 'no_charge' | 'charged';

const QUICK_FILTERS: readonly {
  readonly id: QuickFilter;
  readonly labelHe: string;
  readonly matches: (outcome: string) => boolean;
}[] = [
  { id: 'all', labelHe: 'הכול', matches: () => true },
  { id: 'ready', labelHe: 'מוכנים לחיוב', matches: (o) => o === SELECTABLE_OUTCOME },
  { id: 'attention', labelHe: 'דורשים טיפול', matches: (o) => o.startsWith('blocked_') },
  { id: 'no_charge', labelHe: 'ללא חיוב', matches: (o) => o.startsWith('skipped_') },
  {
    id: 'charged',
    labelHe: 'כבר חויבו',
    matches: (o) => o === 'charged' || o === 'already_charged',
  },
];

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   MONEY AND HEBREW
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

/** `'590.00'` → `59000`. `null` stays `null`: an unpriced row has no amount, and zero is a price. */
export function agorotOf(amount: string | null): number | null {
  if (amount === null) return null;
  const [whole = '0', frac = ''] = amount.split('.');
  const cents = `${frac}00`.slice(0, 2);
  const negative = whole.trimStart().startsWith('-');
  const w = Number(whole.replace('-', '')) || 0;
  return (negative ? -1 : 1) * (w * 100 + (Number(cents) || 0));
}

/** Agorot to the fixed-point string the rest of this file reads. */
function shekelsToFixed(agorot: number): string {
  const sign = agorot < 0 ? '-' : '';
  const abs = Math.abs(agorot);
  return `${sign}${Math.trunc(abs / 100)}.${String(abs % 100).padStart(2, '0')}`;
}

/**
 * ONE AMOUNT, ON ONE LINE, WITH ITS SIGN.
 *
 * **MEASURED IN A BROWSER:** `1287.00` and `₪` wrapped onto two lines in a narrow window, because
 * the cell had no rule against it. The digits run LTR inside an RTL cell and the shekel sign
 * belongs to them, so the whole thing is one unbreakable run.
 */
function Money({ amount }: { readonly amount: string | null }): JSX.Element {
  const agorot = agorotOf(amount);
  if (agorot === null) return <span className="cell-null">—</span>;
  return (
    <span className="cell-amount" dir="ltr">
      {`${shekelsToFixed(agorot)} ₪`}
    </span>
  );
}

/**
 * `2026-10-01` → `01/10/26`. **The Owner, 2026-11-03: "את התאריך של תקופת החיוב תציג DD/MM/YY".**
 *
 * THE DAY FIRST, WHICH IS HOW A DATE IS WRITTEN HERE. The server speaks `YYYY-MM-DD` because that
 * is the one ordering a machine can sort and compare without ambiguity, and it stays that way on
 * the wire; this is the reading end. The column still SORTS on the server's string — `value` is
 * `periodStart` untouched — so a readable date never becomes an unsortable one.
 *
 * IT REFORMATS NOTHING IT CANNOT READ. A value that is not `YYYY-MM-DD` is returned as it came
 * rather than sliced into a wrong date: an empty period on a `skipped_not_due` line is `''`, and
 * `//` would be a date-shaped thing that means nothing.
 */
export function dayMonthYear(iso: string): string {
  const parts = iso.split('-');
  if (parts.length !== 3) return iso;
  const [year = '', month = '', day = ''] = parts;
  if (year.length !== 4 || month.length !== 2 || day.length !== 2) return iso;
  return `${day}/${month}/${year.slice(2)}`;
}

/**
 * THE MONTHS, ABBREVIATED THE WAY THEY ARE WRITTEN IN HEBREW.
 *
 * מרץ, מאי, יוני and יולי ARE NOT ABBREVIATED, because they are already short and a geresh on a
 * three-letter word reads as a mistake rather than as a shortening. The rest carry one.
 */
const MONTHS_HE: readonly string[] = [
  'ינו׳',
  'פבר׳',
  'מרץ',
  'אפר׳',
  'מאי',
  'יוני',
  'יולי',
  'אוג׳',
  'ספט׳',
  'אוק׳',
  'נוב׳',
  'דצמ׳',
];

/**
 * `2026-10-01` … `2026-10-31` → `אוק׳ 26`. **The Owner, 2026-11-03.**
 *
 *     *"בעמודת „תקופת חיוב" הצג חודש מקוצר ושנה בשתי ספרות, למשל „אוק׳ 26"."*
 *
 * A PERIOD IS USUALLY ONE MONTH AND IS NOT ALWAYS. A first period that starts mid-month still ends
 * in the same month and reads the same; one that crosses a boundary is `אוק׳–נוב׳ 26`, and one
 * that crosses the year carries both years. The full range is a hover away either way — this is
 * the label, not the fact.
 *
 * IT REFORMATS NOTHING IT CANNOT READ. An empty period on a `skipped_not_due` line has no month,
 * and inventing one would put a date-shaped thing where there is no date.
 */
function periodLabel(startIso: string, endIso: string): string | null {
  const start = startIso.split('-');
  const end = endIso.split('-');
  if (start.length !== 3 || end.length !== 3) return null;
  const [sy = '', sm = ''] = start;
  const [ey = '', em = ''] = end;
  const sMonth = MONTHS_HE[Number(sm) - 1];
  const eMonth = MONTHS_HE[Number(em) - 1];
  if (sMonth === undefined || eMonth === undefined || sy.length !== 4 || ey.length !== 4) {
    return null;
  }
  if (sy === ey && sm === em) return `${sMonth} ${sy.slice(2)}`;
  if (sy === ey) return `${sMonth}–${eMonth} ${sy.slice(2)}`;
  return `${sMonth} ${sy.slice(2)}–${eMonth} ${ey.slice(2)}`;
}

/**
 * THE PERIOD, SHORT, WITH THE WHOLE TRUTH ONE GESTURE AWAY.
 *
 *     *"עם סמל מידע קטן. במעבר עכבר, במיקוד מהמקלדת או בלחיצה במכשיר מגע, הצג את טווח התאריכים
 *      המלא. כשיש רכיב בחיוב יחסי, ציין בבועית את שם הרכיב ואת מספר ימי החיוב."*
 *
 * ── THREE WAYS IN, AND A `title` ATTRIBUTE IS ONLY ONE OF THEM ────────────────────────────────
 *
 * `title` appears on hover and nowhere else: a keyboard never reaches it and a touch screen has no
 * hover at all, so the operator on a tablet would have a column they cannot read. So the control
 * is a BUTTON — hover and focus open the bubble through CSS, and a press opens it through state,
 * which is the same gesture a tablet makes. `aria-expanded` and `aria-describedby` say which.
 *
 * ── THE PRO-RATA LINES ARE THE REASON THIS EXISTS ─────────────────────────────────────────────
 *
 * A figure that is not a whole month is the one an operator stops at. `chargedDays < periodDays`
 * on a calculation line is exactly that, so the bubble names the component and the days it was
 * charged for — the question *"why is this 250 and not 500"* answered where it is asked, which is
 * what *"בלי לעבור בין מסכים"* means.
 */
function PeriodCell({ line }: { readonly line: RunLine }): JSX.Element {
  const [open, setOpen] = useState(false);

  const label = periodLabel(line.periodStart, line.periodEnd);
  if (label === null) return <span className="cell-null">—</span>;

  const partial = line.calculation.filter((row) => row.chargedDays < row.periodDays);
  const bubbleId = `period-${line.subscriptionUid}`;

  return (
    <span className="period-cell">
      {/*
        ── FOCUS OPENS IT THROUGH STATE, NOT THROUGH CSS. **Measured, 2026-11-03.** ─────────────
        The first version opened on `:focus` in the stylesheet. `:focus` only matches while the
        WINDOW is focused, so nothing could prove the keyboard path from an instrument whose pane
        is not the active window — and a control whose own check cannot open it is a control
        nobody can prove.
        A press on a touch screen FOCUSES the button, so one handler serves both the keyboard and
        the tablet; the mouse keeps the CSS `:hover`, which needs no state and no render.
      */}
      <button
        type="button"
        className="period-cell__label"
        aria-expanded={open}
        aria-describedby={bubbleId}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <span>{label}</span>
        <span className="period-cell__mark" aria-hidden="true">
          ⓘ
        </span>
      </button>
      <span
        className={open ? 'period-cell__bubble is-open' : 'period-cell__bubble'}
        id={bubbleId}
        role="tooltip"
      >
        <span dir="ltr">{`${dayMonthYear(line.periodStart)} — ${dayMonthYear(line.periodEnd)}`}</span>
        {partial.map((row) => (
          <span key={row.skuCode} className="period-cell__partial">
            {`${row.displayNameHe}: ${row.chargedDays} מתוך ${row.periodDays} ימים`}
          </span>
        ))}
      </span>
    </span>
  );
}

/**
 * HEBREW HAS A DUAL, AND A DIGIT IS NOT A SENTENCE.
 *
 * *"1 לקוחות"* is not Hebrew. One customer is לקוח אחד and two are שני לקוחות, neither of which
 * contains a digit — a case this screen's confirmation already learned in a browser, and the
 * notice about hidden selected rows had to learn again.
 */
export function customersHe(count: number): string {
  if (count === 1) return 'לקוח אחד';
  if (count === 2) return 'שני לקוחות';
  return `${count} לקוחות`;
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   THE COLUMNS
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

type ColumnKind = 'text' | 'choice' | 'number' | 'date' | 'money';

interface ColumnSpec {
  readonly id: string;
  readonly labelHe: string;
  readonly kind: ColumnKind;
  /** What the column sorts and ranges on. `null` sorts last whichever direction is chosen. */
  readonly value: (line: RunLine) => number | string | null;
  /** What a text filter searches. */
  readonly text?: (line: RunLine) => string;
  /** What a multi-select offers. One value per row. */
  readonly choice?: (line: RunLine) => string;
  readonly numeric?: true;
}

const COLUMNS: readonly ColumnSpec[] = [
  {
    id: 'customer',
    labelHe: 'מספר לקוח',
    kind: 'text',
    value: (l) => l.tenantDisplayNo,
    /*
     * ONE BOX SEARCHES THE NUMBER AND THE NAME. *"חיפוש לפי שם או מספר לקוח"* — an operator
     * holding a number types it and holding a name types that; asking which box is which is a
     * question the screen can answer for them.
     */
    text: (l) => `${l.tenantDisplayNo ?? ''} ${l.tenantLegalName}`,
    numeric: true,
  },
  {
    id: 'name',
    labelHe: 'שם לקוח',
    kind: 'text',
    value: (l) => l.tenantLegalName,
    text: (l) => l.tenantLegalName,
  },
  {
    id: 'plan',
    labelHe: 'חבילה',
    kind: 'choice',
    value: (l) => l.planNameHe,
    choice: (l) => l.planNameHe,
  },
  { id: 'modules', labelHe: 'מודולים', kind: 'number', value: (l) => l.moduleCount, numeric: true },
  { id: 'companies', labelHe: 'חברות', kind: 'number', value: (l) => l.companyCount, numeric: true },
  { id: 'users', labelHe: 'משתמשים', kind: 'number', value: (l) => l.userCount, numeric: true },
  { id: 'period', labelHe: 'תקופת חיוב', kind: 'date', value: (l) => l.periodStart },
  {
    id: 'exVat',
    labelHe: 'לפני מע״מ',
    kind: 'money',
    value: (l) => agorotOf(l.amountExVat),
    numeric: true,
  },
  { id: 'vat', labelHe: 'מע״מ', kind: 'money', value: (l) => agorotOf(l.vatAmount), numeric: true },
  {
    id: 'total',
    labelHe: 'סכום לחיוב',
    kind: 'money',
    value: (l) => agorotOf(l.amountTotal),
    numeric: true,
  },
  {
    id: 'outcome',
    labelHe: 'מצב',
    kind: 'choice',
    value: (l) => billingOutcomeHe(l.outcome).labelHe,
    choice: (l) => billingOutcomeHe(l.outcome).labelHe,
  },
];

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   THE FILTER STATE
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

interface Filters {
  readonly text: Readonly<Record<string, string>>;
  readonly choice: Readonly<Record<string, readonly string[]>>;
  readonly min: Readonly<Record<string, string>>;
  readonly max: Readonly<Record<string, string>>;
}

const NO_FILTERS: Filters = { text: {}, choice: {}, min: {}, max: {} };

/** Every filter the operator has set, as sentences, so they can see what is in force. */
function activeFilters(filters: Filters): readonly string[] {
  const out: string[] = [];
  for (const column of COLUMNS) {
    const text = filters.text[column.id];
    if (text !== undefined && text.trim() !== '') out.push(`${column.labelHe}: "${text.trim()}"`);
    const chosen = filters.choice[column.id];
    if (chosen !== undefined && chosen.length > 0) {
      out.push(`${column.labelHe}: ${chosen.join(' · ')}`);
    }
    const min = filters.min[column.id];
    const max = filters.max[column.id];
    if ((min !== undefined && min !== '') || (max !== undefined && max !== '')) {
      const from = min === undefined || min === '' ? '…' : min;
      const to = max === undefined || max === '' ? '…' : max;
      out.push(`${column.labelHe}: ${from}–${to}`);
    }
  }
  return out;
}

/** One column's range bound, in the units that column sorts on. `null` when the box is empty. */
function bound(column: ColumnSpec, raw: string | undefined): number | string | null {
  if (raw === undefined || raw.trim() === '') return null;
  if (column.kind === 'money') return agorotOf(raw.trim());
  if (column.kind === 'number') {
    const n = Number(raw.trim());
    return Number.isFinite(n) ? n : null;
  }
  return raw.trim();
}

function matches(line: RunLine, filters: Filters): boolean {
  for (const column of COLUMNS) {
    const text = filters.text[column.id];
    if (text !== undefined && text.trim() !== '' && column.text !== undefined) {
      if (!column.text(line).toLowerCase().includes(text.trim().toLowerCase())) return false;
    }

    const chosen = filters.choice[column.id];
    if (chosen !== undefined && chosen.length > 0 && column.choice !== undefined) {
      if (!chosen.includes(column.choice(line))) return false;
    }

    const low = bound(column, filters.min[column.id]);
    const high = bound(column, filters.max[column.id]);
    if (low !== null || high !== null) {
      const value = column.value(line);
      /*
       * A ROW WITH NO VALUE IS EXCLUDED BY A RANGE, and that is the honest answer rather than the
       * convenient one: an operator filtering *"amount over 100"* is asking for rows that have an
       * amount. Including unpriced rows would put `—` inside a numeric range.
       */
      if (value === null) return false;
      if (low !== null && value < low) return false;
      if (high !== null && value > high) return false;
    }
  }
  return true;
}

function sorted(
  lines: readonly RunLine[],
  sort: { readonly id: string; readonly descending: boolean } | null,
): readonly RunLine[] {
  if (sort === null) return lines;
  const column = COLUMNS.find((c) => c.id === sort.id);
  if (column === undefined) return lines;
  const direction = sort.descending ? -1 : 1;
  return [...lines].sort((a, b) => {
    const left = column.value(a);
    const right = column.value(b);
    /* `null` LAST IN BOTH DIRECTIONS. A row with no figure is not the smallest figure. */
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    if (typeof left === 'number' && typeof right === 'number') return (left - right) * direction;
    return String(left).localeCompare(String(right), 'he') * direction;
  });
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   THE TABLE
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

export function BillingRunTable({
  report,
  picked,
  onToggle,
  onPick,
}: {
  readonly report: RunReport;
  readonly picked: readonly string[];
  readonly onToggle: (uid: string) => void;
  /** Replaces the whole selection — used by the hidden-rows notice and by the header tick. */
  readonly onPick: (uids: readonly string[]) => void;
}): JSX.Element {
  const [quick, setQuick] = useState<QuickFilter>('all');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [sort, setSort] = useState<{ id: string; descending: boolean } | null>(null);
  const [openColumn, setOpenColumn] = useState<string | null>(null);
  const [openRow, setOpenRow] = useState<string | null>(null);

  const quickCounts = useMemo(
    () =>
      new Map(
        QUICK_FILTERS.map((f) => [f.id, report.lines.filter((l) => f.matches(l.outcome)).length]),
      ),
    [report.lines],
  );

  const shown = useMemo(() => {
    const byQuick = report.lines.filter(
      (l) => QUICK_FILTERS.find((f) => f.id === quick)?.matches(l.outcome) ?? true,
    );
    return sorted(
      byQuick.filter((l) => matches(l, filters)),
      sort,
    );
  }, [report.lines, quick, filters, sort]);

  /*
   * THE TOTAL IS OVER EVERY PICKED ROW, SHOWN OR HIDDEN. The figure has to be what the press would
   * charge; a total that silently dropped the hidden ones would be a smaller promise than the act.
   * The count beside it says how many of them are out of sight.
   */
  const pickedTotal = useMemo(() => {
    let agorot = 0;
    for (const line of report.lines) {
      if (line.outcome !== SELECTABLE_OUTCOME) continue;
      if (!picked.includes(line.subscriptionUid)) continue;
      agorot += agorotOf(line.amountTotal) ?? 0;
    }
    return shekelsToFixed(agorot);
  }, [report.lines, picked]);

  /*
   * ── THE SELECTED CUSTOMERS A FILTER IS HIDING ────────────────────────────────────────────────
   *
   *     *"אם סינון מסתיר לקוחות שכבר מסומנים, הצג כמה מהם מוסתרים ותן לי להסיר אותם מהבחירה."*
   *
   * This is the one way this screen could charge somebody the operator never looked at: tick
   * everything, filter down to three customers, press — and the hidden ones go with them. The
   * notice is not a warning in prose; it carries the act that resolves it.
   */
  const visible = new Set(shown.map((l) => l.subscriptionUid));
  const hidden = picked.filter((uid) => !visible.has(uid));
  const chosenShown = shown.filter(
    (l) => l.outcome === SELECTABLE_OUTCOME && picked.includes(l.subscriptionUid),
  );
  const pickedCount = chosenShown.length + hidden.length;

  const active = activeFilters(filters);
  const anyFilter = active.length > 0 || quick !== 'all';

  const selectableShown = shown.filter((l) => l.outcome === SELECTABLE_OUTCOME);
  const allShownPicked =
    selectableShown.length > 0 && selectableShown.every((l) => picked.includes(l.subscriptionUid));

  function setText(id: string, value: string): void {
    setFilters((was) => ({ ...was, text: { ...was.text, [id]: value } }));
  }
  function setRange(id: string, which: 'min' | 'max', value: string): void {
    setFilters((was) => ({ ...was, [which]: { ...was[which], [id]: value } }));
  }
  function toggleChoice(id: string, value: string): void {
    setFilters((was) => {
      const current = was.choice[id] ?? [];
      return {
        ...was,
        choice: {
          ...was.choice,
          [id]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
        },
      };
    });
  }

  if (report.lines.length === 0) {
    return (
      <section className="notice" data-tone="info" data-area="due">
        <h2 className="notice__title">{`לקוחות לתקופה — ${report.asOf}`}</h2>
        <p className="notice__body">
          {`נבדקו ${report.considered} מנויים ולא נמצא אף לקוח. תקופה נגבית רק אחרי שהסתיימה.`}
        </p>
      </section>
    );
  }

  return (
    <section className="notice" data-tone="info" data-area="due">
      <h2 className="notice__title">{`לקוחות לתקופה — ${report.asOf}`}</h2>

      {/* ── THE QUICK FILTERS, EACH WITH ITS COUNT ──────────────────────────────────────────── */}
      <div className="chip-row" data-area="quick-filters">
        {QUICK_FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            className="btn btn--small"
            aria-pressed={quick === f.id}
            onClick={() => setQuick(f.id)}
          >
            {`${f.labelHe} (${quickCounts.get(f.id) ?? 0})`}
          </button>
        ))}
      </div>

      {/* ── WHAT IS IN FORCE, AND ONE PRESS TO CLEAR IT ─────────────────────────────────────── */}
      {!anyFilter ? null : (
        <p className="notice__body" data-area="active-filters">
          {`מסננים פעילים: ${[
            ...(quick === 'all' ? [] : [QUICK_FILTERS.find((f) => f.id === quick)?.labelHe ?? '']),
            ...active,
          ].join(' · ')} `}
          <button
            type="button"
            className="btn btn--small"
            onClick={() => {
              setQuick('all');
              setFilters(NO_FILTERS);
            }}
          >
            ניקוי כל המסננים
          </button>
        </p>
      )}

      {/* ── THE SELECTED ROWS A FILTER IS HIDING ────────────────────────────────────────────── */}
      {hidden.length === 0 ? null : (
        <p className="notice__body" data-area="hidden-picked">
          {`${customersHe(hidden.length)} שמסומנים לחיוב אינם מוצגים במסננים הנוכחיים. `}
          <button
            type="button"
            className="btn btn--small"
            onClick={() => onPick(picked.filter((uid) => visible.has(uid)))}
          >
            הסרת המוסתרים מהבחירה
          </button>
        </p>
      )}

      <p className="notice__body">
        {`מוצגים ${shown.length} מתוך ${report.lines.length} לקוחות. `}
        {report.inferredDays === 0
          ? 'כל ימי התקופות נמדדו.'
          : `${report.inferredDays} ימים לא נמדדו והושלמו לפי המדידה הנמוכה שמסביבם.`}
      </p>

      {/*
        ── THE SYSTEM'S OWN GRID, NOT A TABLE OF MY OWN ──────────────────────────────────────
        See the file header: the first version's class names styled nothing, and the browser laid
        the table out with defaults. `.grid` carries T1–T6 and A4 — the scrollport is inside the
        grid and never on the page, the header sticks and never wraps, and the totals row is last
        and separated.
      */}
      <div className="grid">
        <div className="grid__scroll grid__scroll--billing" data-area="due-table">
          <table className="grid__table">
            <thead className="grid__head">
              <tr>
                <th className="grid__th grid__th--identity" scope="col">
                  <input
                    type="checkbox"
                    aria-label="בחירת כל השורות המוצגות שניתן לחייב"
                    checked={allShownPicked}
                    disabled={selectableShown.length === 0}
                    onChange={() =>
                      onPick(
                        allShownPicked
                          ? picked.filter((uid) => !visible.has(uid))
                          : [
                              ...picked,
                              ...selectableShown
                                .map((l) => l.subscriptionUid)
                                .filter((uid) => !picked.includes(uid)),
                            ],
                      )
                    }
                  />
                </th>
                {COLUMNS.map((column) => (
                  <th
                    key={column.id}
                    className="grid__th"
                    scope="col"
                    {...(column.numeric === true ? { 'data-numeric': 'true' } : {})}
                  >
                    {/*
                      THE FILTER CONTROL SITS BESIDE THE NAME, NOT UNDER IT. The Owner: *"מקם את
                      פקד הסינון ליד שם העמודה כדי לשמור על כותרת קומפקטית."* An inline-flex keeps
                      the header one line high, which is what `grid__th`'s own `white-space:
                      nowrap` already promises for the label.
                    */}
                    <span className="grid__th-controls">
                      <button
                        type="button"
                        className="grid__th-sort"
                        aria-label={`מיון לפי ${column.labelHe}`}
                        onClick={() =>
                          setSort((was) =>
                            was?.id === column.id
                              ? { id: column.id, descending: !was.descending }
                              : { id: column.id, descending: false },
                          )
                        }
                      >
                        {column.labelHe}
                        {sort?.id === column.id ? (sort.descending ? ' ▼' : ' ▲') : ''}
                      </button>
                      <button
                        type="button"
                        className="grid__th-filter-toggle"
                        aria-label={`סינון לפי ${column.labelHe}`}
                        aria-expanded={openColumn === column.id}
                        onClick={() => setOpenColumn(openColumn === column.id ? null : column.id)}
                      >
                        ⌄
                      </button>
                    </span>

                    {openColumn !== column.id ? null : (
                      <div className="grid__th-filter" data-area={`filter-${column.id}`}>
                        {column.kind === 'text' ? (
                          <input
                            className="field__input"
                            aria-label={`חיפוש ב${column.labelHe}`}
                            value={filters.text[column.id] ?? ''}
                            onChange={(e) => setText(column.id, e.currentTarget.value)}
                          />
                        ) : column.kind === 'choice' ? (
                          <>
                            {[...new Set(report.lines.map((l) => column.choice?.(l) ?? ''))]
                              .filter((v) => v !== '')
                              .sort((a, b) => a.localeCompare(b, 'he'))
                              .map((value) => (
                                <label key={value} className="field field--inline">
                                  <input
                                    type="checkbox"
                                    checked={(filters.choice[column.id] ?? []).includes(value)}
                                    onChange={() => toggleChoice(column.id, value)}
                                  />
                                  <span>{value}</span>
                                </label>
                              ))}
                          </>
                        ) : (
                          <>
                            <input
                              className="field__input"
                              aria-label={`${column.labelHe} — מ`}
                              value={filters.min[column.id] ?? ''}
                              onChange={(e) => setRange(column.id, 'min', e.currentTarget.value)}
                            />
                            <input
                              className="field__input"
                              aria-label={`${column.labelHe} — עד`}
                              value={filters.max[column.id] ?? ''}
                              onChange={(e) => setRange(column.id, 'max', e.currentTarget.value)}
                            />
                          </>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {shown.map((line) => {
                const outcome = billingOutcomeHe(line.outcome);
                const selectable = line.outcome === SELECTABLE_OUTCOME;
                const open = openRow === line.subscriptionUid;
                return (
                  <Fragment key={line.subscriptionUid}>
                    <tr className="grid__row">
                      <td className="grid__td grid__td--identity">
                        {/*
                          A BLOCKED ROW KEEPS ITS PLACE AND LOSES THE ABILITY TO BE CHOSEN. The
                          Owner: *"לקוח חסום לחיוב יישאר בטבלה עם הסיבה, ותיבת הבחירה שלו תהיה
                          מושבתת."* Disabled rather than absent, so the column stays aligned.
                        */}
                        <input
                          type="checkbox"
                          aria-label={`בחירת ${line.tenantLegalName}`}
                          checked={picked.includes(line.subscriptionUid)}
                          disabled={!selectable}
                          onChange={() => onToggle(line.subscriptionUid)}
                        />
                      </td>
                      <td className="grid__td" data-numeric="true">
                        {line.tenantDisplayNo ?? <span className="cell-null">—</span>}
                      </td>
                      <td className="grid__td">
                        {line.tenantUid === '' ? (
                          <span dir="ltr">{line.subscriptionUid}</span>
                        ) : (
                          <button
                            type="button"
                            className="grid__row-open"
                            aria-expanded={open}
                            onClick={() => setOpenRow(open ? null : line.subscriptionUid)}
                          >
                            {line.tenantLegalName}
                          </button>
                        )}
                      </td>
                      <td className="grid__td">
                        {line.planNameHe === '' ? (
                          <span className="cell-null">—</span>
                        ) : (
                          line.planNameHe
                        )}
                      </td>
                      <td className="grid__td" data-numeric="true">
                        {line.moduleCount}
                      </td>
                      <td className="grid__td" data-numeric="true">
                        {line.companyCount}
                      </td>
                      <td className="grid__td" data-numeric="true">
                        {line.userCount}
                      </td>
                      <td className="grid__td grid__td--period">
                        {line.periodStart === '' ? (
                          <span className="cell-null">—</span>
                        ) : (
                          <PeriodCell line={line} />
                        )}
                      </td>
                      <td className="grid__td" data-numeric="true">
                        <Money amount={line.amountExVat} />
                      </td>
                      <td className="grid__td" data-numeric="true">
                        <Money amount={line.vatAmount} />
                      </td>
                      <td className="grid__td" data-numeric="true">
                        <strong>
                          <Money amount={line.amountTotal} />
                        </strong>
                      </td>
                      <td className="grid__td">
                        <StatusChip labelHe={outcome.labelHe} tone={outcome.tone} />
                      </td>
                    </tr>
                    {!open ? null : (
                      <tr className="grid__row" data-area="row-detail">
                        <td className="grid__td" colSpan={COLUMNS.length + 1}>
                          <RowDetail line={line} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>

            {/*
              ── THE SUMMARY IS THE TABLE'S LAST ROW, AND IT STAYS ON SCREEN ───────────────────
              The Owner: *"שורת הסיכום תהיה השורה התחתונה של טבלת החיובים עצמה, באותה מסגרת
              וביישור לעמודות. היא תישאר גלויה בתחתית אזור הטבלה בזמן גלילת הלקוחות."*

              A `tfoot` INSIDE the same table is what makes the alignment real: it shares the
              column widths, so it stays in step when the grid scrolls sideways — which a separate
              bar under the table cannot do at any width. T6 already separates a totals row;
              `grid__foot--sticky` is what keeps it at the bottom of the scrollport.
            */}
            <tfoot className="grid__foot grid__foot--sticky" data-area="selection-summary">
              <tr>
                <td className="grid__td grid__td--total grid__td--identity" />
                <td className="grid__td grid__td--total" colSpan={7}>
                  {pickedCount === 0
                    ? 'לא נבחר אף לקוח לחיוב.'
                    : `${pickedCount === 1 ? 'נבחר' : 'נבחרו'} ${customersHe(pickedCount)} לחיוב${
                        hidden.length === 0
                          ? ''
                          : ` · ${customersHe(hidden.length)} מהם אינם מוצגים במסננים הנוכחיים`
                      }`}
                </td>
                <td className="grid__td grid__td--total" colSpan={2}>
                  סך הכול כולל מע״מ
                </td>
                <td className="grid__td grid__td--total" data-numeric="true">
                  <strong>
                    <Money amount={pickedTotal} />
                  </strong>
                </td>
                <td className="grid__td grid__td--total" />
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   ONE ROW'S CALCULATION
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

const WAIVED_REASON_HE: Readonly<Record<string, string>> = {
  included_in_plan: 'כלול בחבילה',
  fully_waived: 'הטבה של 100%',
  not_held: 'לא נרכש בתקופה',
};

/**
 * WHAT THE FIGURE IS MADE OF — INCLUDING THE PARTS THAT COST NOTHING.
 *
 *     *"לכל רכיב הצג מה נרכש, מה כלול בחבילה, מה נוסף בתשלום, הכמות לחיוב... הצג גם רכיבים
 *      שנכללו בחבילה או ניתנו בהטבה של 100%, עם סכום 0 ₪ והסיבה."*
 *
 * The free components come from `line.included`, which the pricing pass records instead of
 * discarding — see `CycleWaivedLine`. Without them a plan that includes four modules showed four
 * fewer rows than the office holds, and the only way to learn why was to open the catalogue.
 */
function RowDetail({ line }: { readonly line: RunLine }): JSX.Element {
  if (line.calculation.length === 0 && line.included.length === 0) {
    return (
      <p className="form__note">
        {line.detail === null ? 'לא נערך חישוב לתקופה הזאת, ולכן אין פירוט להציג.' : line.detail}
      </p>
    );
  }

  return (
    <div className="grid" data-area="row-detail-grid">
      <div className="grid__scroll">
        <table className="grid__table">
          <thead className="grid__head">
            <tr>
              <th className="grid__th" scope="col">
                רכיב
              </th>
              <th className="grid__th" scope="col" data-numeric="true">
                כמות
              </th>
              <th className="grid__th" scope="col" data-numeric="true">
                ימי חיוב
              </th>
              <th className="grid__th" scope="col" data-numeric="true">
                לפני הנחה
              </th>
              <th className="grid__th" scope="col" data-numeric="true">
                הנחה
              </th>
              <th className="grid__th" scope="col" data-numeric="true">
                סכום
              </th>
            </tr>
          </thead>
          <tbody>
            {line.calculation.map((row) => (
              <tr key={row.skuCode} className="grid__row">
                <td className="grid__td">{row.displayNameHe}</td>
                <td className="grid__td" data-numeric="true">
                  {row.quantity}
                </td>
                <td className="grid__td" data-numeric="true">
                  <span dir="ltr">
                    {row.chargedDays === row.periodDays
                      ? `${row.periodDays}`
                      : `${row.chargedDays} / ${row.periodDays}`}
                  </span>
                </td>
                <td className="grid__td" data-numeric="true">
                  <Money amount={row.grossAmount} />
                </td>
                <td className="grid__td" data-numeric="true">
                  <Money amount={row.benefitAmount} />
                </td>
                <td className="grid__td" data-numeric="true">
                  <Money amount={row.amount} />
                </td>
              </tr>
            ))}

            {line.included.map((row) => (
              <tr key={`free-${row.skuCode}`} className="grid__row" data-area="free-component">
                <td className="grid__td">
                  {`${row.displayNameHe} — ${WAIVED_REASON_HE[row.reason] ?? row.reason}`}
                  {row.reason === 'included_in_plan' && row.includedQuantity > 0
                    ? ` (עד ${row.includedQuantity})`
                    : ''}
                </td>
                <td className="grid__td" data-numeric="true">
                  {row.quantity}
                </td>
                <td className="grid__td" data-numeric="true">
                  <span className="cell-null">—</span>
                </td>
                <td className="grid__td" data-numeric="true">
                  <Money amount={row.grossAmount} />
                </td>
                <td className="grid__td" data-numeric="true">
                  <span className="cell-null">—</span>
                </td>
                {/*
                  A REAL ZERO. This component was priced and came to nothing, which is exactly the
                  case the Owner permits `0 ₪` for — unlike an unpriced row, which shows `—`.
                */}
                <td className="grid__td" data-numeric="true">
                  <Money amount="0.00" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {line.documentUid === null || line.tenantUid === '' ? null : (
        <p className="form__note">
          {/*
            THE CUSTOMER'S CARD AND NOT THE DOCUMENT ITSELF. 08 §12B.1 gives the platform *"view
            billing & subscription history"* and gives the DOWNLOAD to the tenant. The same rule
            the results table on this screen already follows.
          */}
          <a href={`${routes.platformTenant(line.tenantUid)}#billing`}>המסמך בכרטיס הלקוח</a>
        </p>
      )}
    </div>
  );
}
